/**
 * Share Book Hooks (Digital aktiebok)
 *
 * React Query hooks for managing digital share books according to ABL 5 kap.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  shareBookApi,
  type CreateShareBookData,
  type UpdateShareBookData,
  type CreateShareClassData,
  type UpdateShareClassData,
  type CreateShareholderData,
  type UpdateShareholderData,
  type FoundingTransactionData,
  type PurchaseTransactionData,
  type GiftTransactionData,
  type InheritanceTransactionData,
  type NewIssueTransactionData,
  type BonusIssueTransactionData,
  type SplitTransactionData,
  type ReverseSplitTransactionData,
  type ReductionTransactionData,
  type ConversionTransactionData,
  type RedemptionTransactionData,
  type PledgeTransactionData,
  type UnpledgeTransactionData,
  type TransactionType,
} from "@/lib/api";

// ═══════════════════════════════════════════════════════════════════
// Query Keys
// ═══════════════════════════════════════════════════════════════════

export const shareBookKeys = {
  all: ["shareBook"] as const,

  // Share Book
  book: (companyId: string) => [...shareBookKeys.all, "book", companyId] as const,
  shareholdersSummary: (companyId: string) =>
    [...shareBookKeys.all, "shareholdersSummary", companyId] as const,
  recentTransactions: (companyId: string, limit?: number) =>
    [...shareBookKeys.all, "recentTransactions", companyId, limit] as const,

  // Share Classes
  shareClasses: (companyId: string) =>
    [...shareBookKeys.all, "shareClasses", companyId] as const,
  shareClass: (shareClassId: string) =>
    [...shareBookKeys.all, "shareClass", shareClassId] as const,

  // Shareholders
  shareholders: (companyId: string, params?: { includeInactive?: boolean; search?: string }) =>
    [...shareBookKeys.all, "shareholders", companyId, params] as const,
  shareholder: (shareholderId: string) =>
    [...shareBookKeys.all, "shareholder", shareholderId] as const,
  shareholderHoldings: (shareholderId: string) =>
    [...shareBookKeys.all, "shareholderHoldings", shareholderId] as const,

  // Transactions
  transactions: (companyId: string, params?: { limit?: number; offset?: number; type?: TransactionType }) =>
    [...shareBookKeys.all, "transactions", companyId, params] as const,
  transaction: (transactionId: string) =>
    [...shareBookKeys.all, "transaction", transactionId] as const,

  // Events
  events: (companyId: string, params?: { limit?: number; offset?: number; eventType?: string }) =>
    [...shareBookKeys.all, "events", companyId, params] as const,
  eventsAsOfDate: (companyId: string, date: string) =>
    [...shareBookKeys.all, "eventsAsOfDate", companyId, date] as const,
};

// ═══════════════════════════════════════════════════════════════════
// Share Book Queries
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch share book summary for a company.
 * Returns null if no share book exists yet.
 */
export function useShareBook(companyId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.book(companyId || ""),
    queryFn: async () => {
      if (!companyId) return null;
      const result = await shareBookApi.get(companyId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktiebok");
      }
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: "always", // Always refetch when component mounts to ensure fresh data
    // Don't retry on subscription/activation errors - these require user action
    retry: (failureCount, error) => {
      const errorMessage = error instanceof Error ? error.message : "";
      if (errorMessage.includes("aktiverat") || errorMessage.includes("prenumeration")) {
        return false; // Don't retry subscription errors
      }
      return failureCount < 3; // Default retry behavior for other errors
    },
  });
}

/**
 * Fetch shareholders with their holdings summary.
 */
export function useShareholdersSummary(companyId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: shareBookKeys.shareholdersSummary(companyId || ""),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.getShareholdersSummary(companyId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktieägare");
      }
      return result.data || [];
    },
    enabled: !!companyId && (options?.enabled !== false),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch recent transactions for a company.
 */
export function useRecentTransactions(companyId: string | undefined, limit = 10, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: shareBookKeys.recentTransactions(companyId || "", limit),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.getRecentTransactions(companyId, limit);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta transaktioner");
      }
      return result.data || [];
    },
    enabled: !!companyId && (options?.enabled !== false),
    staleTime: 30 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════════
// Share Book Mutations
// ═══════════════════════════════════════════════════════════════════

/**
 * Create a new share book for a company.
 */
export function useCreateShareBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateShareBookData;
    }) => {
      const result = await shareBookApi.create(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte skapa aktiebok");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
    },
  });
}

/**
 * Update share book settings.
 */
export function useUpdateShareBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: UpdateShareBookData;
    }) => {
      const result = await shareBookApi.update(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte uppdatera aktiebok");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
    },
  });
}

/**
 * Delete share book and all related data.
 * Use force: true to bypass active holdings check (for testing purposes).
 */
export function useDeleteShareBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, force }: { companyId: string; force?: boolean }) => {
      const result = await shareBookApi.delete(companyId, { force });
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte radera aktiebok");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      // Invalidate all share book related queries
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClasses(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholders(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.transactions(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.events(companyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// Share Class Queries & Mutations
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch all share classes for a company.
 */
export function useShareClasses(companyId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.shareClasses(companyId || ""),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.shareClasses.list(companyId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktieslag");
      }
      return result.data || [];
    },
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch a single share class.
 */
export function useShareClass(shareClassId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.shareClass(shareClassId || ""),
    queryFn: async () => {
      if (!shareClassId) return null;
      const result = await shareBookApi.shareClasses.get(shareClassId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktieslag");
      }
      return result.data;
    },
    enabled: !!shareClassId,
  });
}

/**
 * Create a new share class.
 */
export function useCreateShareClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateShareClassData;
    }) => {
      const result = await shareBookApi.shareClasses.create(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte skapa aktieslag");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClasses(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
    },
  });
}

/**
 * Update a share class.
 */
export function useUpdateShareClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shareClassId,
      data,
      companyId: _companyId,
    }: {
      shareClassId: string;
      data: UpdateShareClassData;
      companyId: string;
    }) => {
      void _companyId; // Used in onSuccess for cache invalidation
      const result = await shareBookApi.shareClasses.update(shareClassId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte uppdatera aktieslag");
      }
      return result.data!;
    },
    onSuccess: (_, { shareClassId, companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClass(shareClassId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClasses(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
    },
  });
}

/**
 * Delete a share class.
 */
export function useDeleteShareClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shareClassId,
      companyId: _companyId,
    }: {
      shareClassId: string;
      companyId: string;
    }) => {
      void _companyId; // Used in onSuccess for cache invalidation
      const result = await shareBookApi.shareClasses.delete(shareClassId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte ta bort aktieslag");
      }
      return result.data;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClasses(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// Shareholder Queries & Mutations
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch all shareholders for a company.
 */
export function useShareholders(
  companyId: string | undefined,
  params?: { includeInactive?: boolean; search?: string }
) {
  return useQuery({
    queryKey: shareBookKeys.shareholders(companyId || "", params),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.shareholders.list(companyId, params);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktieägare");
      }
      return result.data || [];
    },
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch a single shareholder.
 */
export function useShareholder(shareholderId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.shareholder(shareholderId || ""),
    queryFn: async () => {
      if (!shareholderId) return null;
      const result = await shareBookApi.shareholders.get(shareholderId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta aktieägare");
      }
      return result.data;
    },
    enabled: !!shareholderId,
  });
}

/**
 * Fetch a shareholder's holdings.
 */
export function useShareholderHoldings(shareholderId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.shareholderHoldings(shareholderId || ""),
    queryFn: async () => {
      if (!shareholderId) return [];
      const result = await shareBookApi.shareholders.getHoldings(shareholderId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta innehav");
      }
      return result.data || [];
    },
    enabled: !!shareholderId,
  });
}

/**
 * Create a new shareholder.
 */
export function useCreateShareholder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateShareholderData;
    }) => {
      const result = await shareBookApi.shareholders.create(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte skapa aktieägare");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholders(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
    },
  });
}

/**
 * Update a shareholder.
 */
export function useUpdateShareholder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shareholderId,
      data,
      companyId: _companyId,
    }: {
      shareholderId: string;
      data: UpdateShareholderData;
      companyId: string;
    }) => {
      void _companyId; // Used in onSuccess for cache invalidation
      const result = await shareBookApi.shareholders.update(shareholderId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte uppdatera aktieägare");
      }
      return result.data!;
    },
    onSuccess: (_, { shareholderId, companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholder(shareholderId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholders(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
    },
  });
}

/**
 * Deactivate (soft delete) a shareholder.
 */
export function useDeleteShareholder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shareholderId,
      companyId: _companyId,
    }: {
      shareholderId: string;
      companyId: string;
    }) => {
      void _companyId; // Used in onSuccess for cache invalidation
      const result = await shareBookApi.shareholders.delete(shareholderId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte ta bort aktieägare");
      }
      return result.data;
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholders(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// Transaction Queries & Mutations
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch all transactions for a company.
 */
export function useTransactions(
  companyId: string | undefined,
  params?: { limit?: number; offset?: number; type?: TransactionType }
) {
  return useQuery({
    queryKey: shareBookKeys.transactions(companyId || "", params),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.transactions.list(companyId, params);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta transaktioner");
      }
      return result.data || [];
    },
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch a single transaction.
 */
export function useTransaction(transactionId: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.transaction(transactionId || ""),
    queryFn: async () => {
      if (!transactionId) return null;
      const result = await shareBookApi.transactions.get(transactionId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta transaktion");
      }
      return result.data;
    },
    enabled: !!transactionId,
  });
}

// Helper to invalidate all transaction-related queries
function invalidateTransactionQueries(queryClient: ReturnType<typeof useQueryClient>, companyId: string) {
  // Use partial key matching to invalidate all transactions queries regardless of params
  queryClient.invalidateQueries({
    queryKey: [...shareBookKeys.all, "transactions", companyId],
  });
  queryClient.invalidateQueries({
    queryKey: [...shareBookKeys.all, "recentTransactions", companyId],
  });
  queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
  queryClient.invalidateQueries({ queryKey: shareBookKeys.shareClasses(companyId) });
  queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
  // Also invalidate shareholder holdings since they change with transactions
  queryClient.invalidateQueries({
    queryKey: [...shareBookKeys.all, "shareholderHoldings"],
  });
}

/**
 * Register a founding transaction (Bildande).
 */
export function useRegisterFoundingTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: FoundingTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerFounding(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera grundande");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a purchase transaction (Köp).
 */
export function useRegisterPurchaseTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: PurchaseTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerPurchase(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera köp");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a gift transaction (Gåva).
 */
export function useRegisterGiftTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: GiftTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerGift(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera gåva");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register an inheritance transaction (Arv).
 */
export function useRegisterInheritanceTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: InheritanceTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerInheritance(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera arv");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a new issue transaction (Nyemission).
 */
export function useRegisterNewIssueTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: NewIssueTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerNewIssue(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera nyemission");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Generic hook for registering any transaction type.
 * Routes to the appropriate API method based on transaction type.
 */
export interface GenericTransactionData {
  type: TransactionType;
  shareClassId: string;
  fromShareholderId?: string;
  toShareholderId?: string;
  quantity: number;
  numberFrom?: number;
  numberTo?: number;
  pricePerShare?: number;
  totalAmount?: number;
  transactionDate: string;
  notes?: string;
  internalNotes?: string;
  preemptionWaived?: boolean;
  consentObtained?: boolean;
  deceasedDate?: string;
  estateInventoryDate?: string;
  registrationDate?: string;
}

export function useRegisterTransaction(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GenericTransactionData) => {
      let result;

      switch (data.type) {
        case "founding":
          result = await shareBookApi.transactions.registerFounding(companyId, {
            shareClassId: data.shareClassId,
            toShareholderId: data.toShareholderId!,
            quantity: data.quantity,
            pricePerShare: data.pricePerShare || 0,
            transactionDate: data.transactionDate,
            notes: data.notes,
          });
          break;

        case "purchase":
          result = await shareBookApi.transactions.registerPurchase(companyId, {
            shareClassId: data.shareClassId,
            fromShareholderId: data.fromShareholderId!,
            toShareholderId: data.toShareholderId!,
            numberFrom: data.numberFrom || 1,
            numberTo: data.numberTo || data.quantity,
            pricePerShare: data.pricePerShare || 0,
            transactionDate: data.transactionDate,
            preemptionWaived: data.preemptionWaived,
            consentObtained: data.consentObtained,
            notes: data.notes,
          });
          break;

        case "gift":
          result = await shareBookApi.transactions.registerGift(companyId, {
            shareClassId: data.shareClassId,
            fromShareholderId: data.fromShareholderId!,
            toShareholderId: data.toShareholderId!,
            numberFrom: data.numberFrom || 1,
            numberTo: data.numberTo || data.quantity,
            transactionDate: data.transactionDate,
            preemptionWaived: data.preemptionWaived,
            consentObtained: data.consentObtained,
            notes: data.notes,
          });
          break;

        case "inheritance":
          result = await shareBookApi.transactions.registerInheritance(companyId, {
            shareClassId: data.shareClassId,
            fromShareholderId: data.fromShareholderId!,
            toShareholderId: data.toShareholderId!,
            numberFrom: data.numberFrom || 1,
            numberTo: data.numberTo || data.quantity,
            transactionDate: data.transactionDate,
            deceasedDate: data.deceasedDate,
            estateInventoryDate: data.estateInventoryDate,
            notes: data.notes,
          });
          break;

        case "new_issue":
          result = await shareBookApi.transactions.registerNewIssue(companyId, {
            shareClassId: data.shareClassId,
            toShareholderId: data.toShareholderId!,
            quantity: data.quantity,
            pricePerShare: data.pricePerShare || 0,
            transactionDate: data.transactionDate,
            registrationDate: data.registrationDate,
            notes: data.notes,
          });
          break;

        default:
          throw new Error(`Transaktionstyp '${data.type}' stöds inte ännu`);
      }

      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera transaktion");
      }
      return result.data!;
    },
    onSuccess: () => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Verify a transaction.
 */
export function useVerifyTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      companyId: _companyId,
    }: {
      transactionId: string;
      companyId: string;
    }) => {
      void _companyId; // Used in onSuccess for cache invalidation
      const result = await shareBookApi.transactions.verify(transactionId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte verifiera transaktion");
      }
      return result.data!;
    },
    onSuccess: (_, { transactionId, companyId }) => {
      queryClient.invalidateQueries({ queryKey: shareBookKeys.transaction(transactionId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.transactions(companyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// Event Queries (Audit Trail)
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch events (audit trail) for a share book.
 */
export function useShareBookEvents(
  companyId: string | undefined,
  params?: { limit?: number; offset?: number; eventType?: string; entityType?: string; entityId?: string }
) {
  return useQuery({
    queryKey: shareBookKeys.events(companyId || "", params),
    queryFn: async () => {
      if (!companyId) return [];
      const result = await shareBookApi.events.list(companyId, params);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta händelselogg");
      }
      return result.data || [];
    },
    enabled: !!companyId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch events as of a specific date (historical snapshot).
 */
export function useShareBookEventsAsOfDate(companyId: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: shareBookKeys.eventsAsOfDate(companyId || "", date || ""),
    queryFn: async () => {
      if (!companyId || !date) return [];
      const result = await shareBookApi.events.getAsOfDate(companyId, date);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta händelser");
      }
      return result.data || [];
    },
    enabled: !!companyId && !!date,
  });
}

/**
 * Verify the integrity of an event.
 */
export function useVerifyEventIntegrity(eventId: string | undefined) {
  return useQuery({
    queryKey: [...shareBookKeys.all, "eventIntegrity", eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const result = await shareBookApi.events.verifyIntegrity(eventId);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte verifiera händelse");
      }
      return result.data;
    },
    enabled: !!eventId,
  });
}

// ═══════════════════════════════════════════════════════════════════
// Bulk Import
// ═══════════════════════════════════════════════════════════════════

/**
 * Bulk import shareholders from CSV.
 */
export function useBulkImportShareholders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, csvData }: { companyId: string; csvData: string }) => {
      const result = await shareBookApi.shareholders.bulkImport(companyId, csvData);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte importera aktieägare");
      }
      return result.data;
    },
    onSuccess: (_, { companyId }) => {
      // Invalidate shareholder queries
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholders(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.shareholdersSummary(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId) });
      queryClient.invalidateQueries({ queryKey: shareBookKeys.events(companyId) });
    },
  });
}

/**
 * Get CSV import template.
 */
export function useGetImportTemplate(companyId: string | undefined) {
  return useQuery({
    queryKey: [...shareBookKeys.all, "importTemplate", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      return shareBookApi.shareholders.getImportTemplate(companyId);
    },
    enabled: !!companyId,
    staleTime: Infinity, // Template never changes
  });
}

// ═══════════════════════════════════════════════════════════════════
// Advanced Transaction Mutations (Capital Changes & Other)
// ═══════════════════════════════════════════════════════════════════

/**
 * Register a bonus issue transaction (Fondemission).
 */
export function useRegisterBonusIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: BonusIssueTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerBonusIssue(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera fondemission");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a split transaction (Aktiesplit).
 */
export function useRegisterSplit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: SplitTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerSplit(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera aktiesplit");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a reverse split transaction (Omvänd split).
 */
export function useRegisterReverseSplit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: ReverseSplitTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerReverseSplit(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera omvänd split");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a capital reduction transaction (Kapitalreduktion).
 */
export function useRegisterReduction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: ReductionTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerReduction(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera kapitalreduktion");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a share class conversion transaction (Omvandling).
 */
export function useRegisterConversion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: ConversionTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerConversion(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera omvandling");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a redemption transaction (Inlösen).
 */
export function useRegisterRedemption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: RedemptionTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerRedemption(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera inlösen");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register a pledge transaction (Pantsättning).
 */
export function useRegisterPledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: PledgeTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerPledge(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte registrera pantsättning");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}

/**
 * Register an unpledge transaction (Hävning av pantsättning).
 */
export function useRegisterUnpledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      data,
    }: {
      companyId: string;
      data: UnpledgeTransactionData;
    }) => {
      const result = await shareBookApi.transactions.registerUnpledge(companyId, data);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte häva pantsättning");
      }
      return result.data!;
    },
    onSuccess: (_, { companyId }) => {
      invalidateTransactionQueries(queryClient, companyId);
    },
  });
}
